import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { useAnlagen, useAnmeldungVorbereiten } from "../api/hooks";
import type { AnlageStatus, AnmeldungVorbereitenErgebnis } from "../api/typen";
import { Hinweis, Karte, Knopf, Modal, Spinner, StatusBadge } from "../komponenten/Basis";
import { useToast } from "../kontext/ToastKontext";
import { MASTR_STATUS_TEXT, NETZBETREIBER_STATUS_TEXT, STATUS_TEXT, kwp, zeitpunkt } from "../lib/format";

const SEITENGROESSE = 25;
const FILTER: (AnlageStatus | "alle")[] = ["alle", "draft", "validiert", "final", "angemeldet"];

function csvWert(wert: unknown): string {
  const text = wert === null || wert === undefined ? "" : String(wert);
  // Schutz vor Formel-Injection in Excel (=, +, -, @ am Anfang)
  const sicher = /^[=+\-@]/.test(text) ? `'${text}` : text;
  return /[";\n]/.test(sicher) ? `"${sicher.replace(/"/g, '""')}"` : sicher;
}

function csvHerunterladen(ergebnis: AnmeldungVorbereitenErgebnis) {
  const bereite = ergebnis.anlagen.filter((a) => a.bereit);
  const spalten = ["anlage_id", "betreiber", "email", "leistung_kwp", "modultyp", "wechselrichter", "strasse", "plz", "ort", "zaehlernummer", "zerez_id", "vollmacht_datum"];
  const zeilen = bereite.map((a) => spalten.map((s) => csvWert(s === "anlage_id" ? a.anlage_id : a.daten[s])).join(";"));
  const inhalt = "\uFEFF" + [spalten.join(";"), ...zeilen].join("\r\n");
  const url = URL.createObjectURL(new Blob([inhalt], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = `anmeldung_vorbereitet_${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

export function AnlagenSeite() {
  const [parameter, setParameter] = useSearchParams();
  const status = (parameter.get("status") as AnlageStatus | null) ?? undefined;
  const seite = Math.max(0, Number(parameter.get("seite") ?? 0) || 0);
  const nurNeue = parameter.get("neu") === "1";
  const abfrage = useAnlagen({ status, neueUnterlagen: nurNeue, limit: SEITENGROESSE, offset: seite * SEITENGROESSE });
  const [auswahl, setAuswahl] = useState<Set<number>>(new Set());
  const vorbereiten = useAnmeldungVorbereiten();
  const [bericht, setBericht] = useState<AnmeldungVorbereitenErgebnis | null>(null);
  const toast = useToast();

  const items = abfrage.data?.items ?? [];
  const total = abfrage.data?.total ?? 0;
  const alleGewaehlt = items.length > 0 && items.every((a) => auswahl.has(a.id));

  function filterSetzen(neu: AnlageStatus | "alle") {
    const p = new URLSearchParams();
    if (neu !== "alle") p.set("status", neu);
    if (nurNeue) p.set("neu", "1");
    setParameter(p);
    setAuswahl(new Set());
  }

  function neueUmschalten() {
    const p = new URLSearchParams();
    if (status) p.set("status", status);
    if (!nurNeue) p.set("neu", "1");
    setParameter(p);
    setAuswahl(new Set());
  }

  function umschalten(id: number) {
    setAuswahl((alt) => {
      const neu = new Set(alt);
      if (neu.has(id)) neu.delete(id);
      else neu.add(id);
      return neu;
    });
  }

  function vorbereitenStarten() {
    vorbereiten.mutate([...auswahl], {
      onSuccess: (ergebnis) => {
        setBericht(ergebnis);
        toast.info("Prüfung abgeschlossen", `${ergebnis.bereit} bereit · ${ergebnis.nicht_bereit} mit offenen Punkten`);
      },
      onError: (e) => toast.fehler(e, "Vorbereitung fehlgeschlagen"),
    });
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Anlagen-Übersicht</h1>
          <p className="mt-1 text-sm text-neutral-600">
            {total} Anlage(n){status ? ` mit Status „${STATUS_TEXT[status]}“` : ""}{nurNeue ? " mit neuen Unterlagen" : ""}
          </p>
        </div>
        <Knopf variante="primaer" disabled={auswahl.size === 0} laedt={vorbereiten.isPending} onClick={vorbereitenStarten}>
          Mehrere für Anmeldung vorbereiten{auswahl.size ? ` (${auswahl.size})` : ""}
        </Knopf>
      </div>

      <button
        type="button"
        aria-pressed={nurNeue}
        onClick={neueUmschalten}
        className={`rounded-lg border px-4 py-1.5 text-sm font-semibold transition ${
          nurNeue ? "border-ampel-gelb bg-ampel-gelbHell text-ampel-gelb" : "border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50"
        }`}
      >
        Neue Unterlagen{nurNeue ? " ✓" : ""}
      </button>

      <div className="flex flex-wrap gap-1 rounded-xl bg-neutral-100 p-1" role="tablist" aria-label="Nach Status filtern">
        {FILTER.map((f) => {
          const aktiv = (f === "alle" && !status) || f === status;
          return (
            <button
              key={f}
              type="button"
              role="tab"
              aria-selected={aktiv}
              onClick={() => filterSetzen(f)}
              className={`rounded-lg px-4 py-1.5 text-sm font-semibold transition ${aktiv ? "bg-white shadow-sm" : "text-neutral-600 hover:text-neutral-900"}`}
            >
              {f === "alle" ? "Alle" : STATUS_TEXT[f]}
            </button>
          );
        })}
      </div>

      <Karte className="overflow-hidden">
        {abfrage.isLoading ? (
          <div className="flex justify-center py-16"><Spinner label="Anlagen werden geladen…" /></div>
        ) : abfrage.error ? (
          <div className="p-6">
            <Hinweis art="fehler">{abfrage.error.message}</Hinweis>
            <Knopf className="mt-3" onClick={() => void abfrage.refetch()}>Erneut laden</Knopf>
          </div>
        ) : items.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <p className="text-3xl" aria-hidden>🗂️</p>
            <p className="mt-2 font-semibold">Keine Anlagen gefunden</p>
            <p className="mt-1 text-sm text-neutral-500">Lade Dokumente hoch, dann entsteht automatisch eine Anlage.</p>
            <Link to="/" className="mt-4 inline-block rounded-lg bg-marke px-4 py-2 text-sm font-semibold text-white">Zum Upload</Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] text-left text-sm">
              <thead className="border-b border-neutral-200 bg-neutral-50 text-xs uppercase tracking-wide text-neutral-500">
                <tr>
                  <th className="w-10 px-4 py-3">
                    <input
                      type="checkbox"
                      aria-label="Alle auf dieser Seite auswählen"
                      checked={alleGewaehlt}
                      onChange={() => setAuswahl(alleGewaehlt ? new Set() : new Set(items.map((a) => a.id)))}
                    />
                  </th>
                  <th className="px-4 py-3">Kunde</th>
                  <th className="px-4 py-3">Leistung</th>
                  <th className="px-4 py-3">ZEREZ-ID</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Vollmacht-OK</th>
                  <th className="px-4 py-3">MaStR</th>
                  <th className="px-4 py-3">Offene Punkte</th>
                  <th className="px-4 py-3 text-right">Aktion</th>
                </tr>
              </thead>
              <tbody className={`divide-y divide-neutral-100 ${abfrage.isPlaceholderData ? "opacity-60" : ""}`}>
                {items.map((a) => {
                  const offen = a.niedrige_konfidenz_felder + a.fehlende_pflichtfelder;
                  return (
                    <tr key={a.id} className={`hover:bg-neutral-50 ${auswahl.has(a.id) ? "bg-neutral-50" : ""}`}>
                      <td className="px-4 py-3">
                        <input type="checkbox" aria-label={`Anlage ${a.id} auswählen`} checked={auswahl.has(a.id)} onChange={() => umschalten(a.id)} />
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-semibold">
                          {a.kunde.name}
                          {a.neue_unterlagen > 0 && (
                            <span className="ml-2 whitespace-nowrap rounded-full bg-ampel-gelb px-2 py-0.5 align-middle text-[11px] font-bold text-white">
                              neue Unterlagen ({a.neue_unterlagen})
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-neutral-500">
                          #{a.id} · {[a.plz, a.ort].filter(Boolean).join(" ") || "Ort unbekannt"} · {a.dokumente_anzahl} Dok.
                          {a.betrieb_name && ` · ${a.betrieb_name}`}
                        </p>
                      </td>
                      <td className="px-4 py-3 tabular-nums">{kwp(a.leistung_kwp)}</td>
                      <td className="px-4 py-3 font-mono text-xs">{a.zerez_id ?? <span className="font-sans text-neutral-400">–</span>}</td>
                      <td className="px-4 py-3"><StatusBadge status={a.status} /></td>
                      <td className="px-4 py-3">
                        {a.vollmacht_ok ? (
                          <span className="font-semibold text-ampel-gruen">✓ Ja</span>
                        ) : (
                          <span className="font-semibold text-ampel-rot">{a.vollmacht_vorhanden ? "✗ nicht unterschrieben" : "✗ fehlt"}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs">
                        <span className={a.mastr_status === "registriert" ? "font-semibold text-ampel-gruen" : a.mastr_status === "in_bearbeitung" ? "text-ampel-gelb" : "text-neutral-500"}>
                          {MASTR_STATUS_TEXT[a.mastr_status]}
                        </span>
                        <span className="block text-neutral-400">Netz: {NETZBETREIBER_STATUS_TEXT[a.netzbetreiber_status]}</span>
                      </td>
                      <td className="px-4 py-3 text-xs">
                        {a.status === "final" || a.status === "angemeldet" ? (
                          <span className="text-neutral-400">–</span>
                        ) : offen === 0 ? (
                          <span className="text-ampel-gruen">keine</span>
                        ) : (
                          <span className="text-ampel-gelb">
                            {a.fehlende_pflichtfelder > 0 && `${a.fehlende_pflichtfelder} fehlt`}
                            {a.fehlende_pflichtfelder > 0 && a.niedrige_konfidenz_felder > 0 && " · "}
                            {a.niedrige_konfidenz_felder > 0 && `${a.niedrige_konfidenz_felder} unsicher`}
                          </span>
                        )}
                        <span className="block text-neutral-400">{zeitpunkt(a.updated_at)}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {a.status === "final" ? (
                          <Link to={`/anlagen/${a.id}/mastr`} className="inline-block rounded-lg bg-marke px-3 py-1.5 text-sm font-semibold text-white hover:bg-marke-hover">
                            MaStR →
                          </Link>
                        ) : (
                          <Link
                            to={`/anlagen/${a.id}`}
                            className={`inline-block rounded-lg px-3 py-1.5 text-sm font-semibold ${
                              a.status === "angemeldet" ? "border border-neutral-300 hover:bg-neutral-50" : "bg-marke text-white hover:bg-marke-hover"
                            }`}
                          >
                            {a.status === "angemeldet" ? "Ansehen" : "Prüfen"}
                          </Link>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {total > SEITENGROESSE && (
          <div className="flex items-center justify-between border-t border-neutral-200 px-4 py-3 text-sm">
            <span className="text-neutral-500">
              {seite * SEITENGROESSE + 1}–{Math.min(total, (seite + 1) * SEITENGROESSE)} von {total}
            </span>
            <div className="flex gap-2">
              <Knopf disabled={seite === 0} onClick={() => setParameter((p) => { p.set("seite", String(seite - 1)); return p; })}>← Zurück</Knopf>
              <Knopf disabled={(seite + 1) * SEITENGROESSE >= total} onClick={() => setParameter((p) => { p.set("seite", String(seite + 1)); return p; })}>Weiter →</Knopf>
            </div>
          </div>
        )}
      </Karte>

      <Modal
        offen={bericht !== null}
        breit
        titel="Anmeldung vorbereiten – Ergebnis"
        beiSchliessen={() => setBericht(null)}
        fuss={
          <>
            <Knopf onClick={() => setBericht(null)}>Schließen</Knopf>
            <Knopf variante="primaer" disabled={!bericht?.bereit} onClick={() => bericht && csvHerunterladen(bericht)}>
              CSV exportieren ({bericht?.bereit ?? 0} bereit)
            </Knopf>
          </>
        }
      >
        {bericht && (
          <div className="space-y-3">
            <p className="text-sm text-neutral-600">
              Geprüft werden Status, Pflichtangaben und Vollmacht. Die automatische Übermittlung an MaStR und Netzbetreiber folgt in Phase 2.
              Bis dahin enthält der CSV-Export die Anlagen, die bereit sind (ohne IBAN).
            </p>
            <ul className="divide-y divide-neutral-100 rounded-xl border border-neutral-200">
              {bericht.anlagen.map((a) => (
                <li key={a.anlage_id} className="flex items-start gap-3 px-4 py-3">
                  <span className={`mt-0.5 text-lg ${a.bereit ? "text-ampel-gruen" : "text-ampel-rot"}`} aria-hidden>{a.bereit ? "✓" : "✗"}</span>
                  <div className="min-w-0 flex-1 text-sm">
                    <p className="font-semibold">
                      <Link to={`/anlagen/${a.anlage_id}`} className="hover:underline">Anlage #{a.anlage_id} · {a.kunde.name}</Link>{" "}
                      <StatusBadge status={a.status} />
                    </p>
                    {a.probleme.map((p) => <p key={p} className="text-ampel-rot">• {p}</p>)}
                    {a.hinweise.map((h) => <p key={h} className="text-ampel-gelb">• {h}</p>)}
                    {a.bereit && a.hinweise.length === 0 && <p className="text-ampel-gruen">Bereit für die Anmeldung</p>}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Modal>
    </div>
  );
}
